import { useEffect, useMemo, useState } from 'react'
import type { CommLogEntryFields } from 'app/types/comm-log'
import { getPmsCommTypeName } from 'app/components/LazyPmsCommTypeCell'
import { getPmsUserName } from 'app/components/LazyPmsUserCell'

const COMM_TYPE = `type`
const USER = `user`

const keyFor = (kind: string, locationId: string, code: number) => `${kind}:${locationId}:${code}`

/**
 * Resolves the numeric `commType` and `userNum` on a page of comm log entries
 * into names, returning the entries decorated.
 *
 * The lookups themselves live in the Lazy PMS cells, which the contacts table
 * uses. Sharing them means one cache for the whole app: browse the table, then
 * open a drawer, and the names are already there. It also keeps one definition
 * of the two queries TRK-5572 and TRK-5573 added, which arrived as root lookups
 * taking a single code rather than fields on the record.
 *
 * Resolving here rather than per row because a row is not the unit anyone reads:
 * a hundred entries share a handful of codes, and the feed should not mount a
 * hundred lazy cells to discover that.
 *
 * Entries come back undecorated first and gain their labels a moment later.
 * That is deliberate: the note text is the substance of the record and should
 * not wait on a chip.
 */
export function usePmsCommLogLabels(entries: CommLogEntryFields[]): CommLogEntryFields[] {
  const [resolved, setResolved] = useState<Record<string, string>>({})

  // Only what this page actually needs, deduped. Codes of 0 are excluded: the
  // PMS uses 0 for "unset" and both resolvers return null for it.
  const wanted = useMemo(() => {
    const keys = new Set<string>()
    for (const entry of entries) {
      if (!entry.locationId) continue
      if (entry.commType) keys.add(keyFor(COMM_TYPE, entry.locationId, entry.commType))
      if (entry.userNum) keys.add(keyFor(USER, entry.locationId, entry.userNum))
    }
    return [...keys].sort()
  }, [entries])

  const wantedKey = wanted.join(`|`)

  useEffect(() => {
    let cancelled = false

    const lookups = wanted.map(async (key) => {
      const [kind, locationId, code] = key.split(`:`)
      const num = Number(code)
      const name =
        kind === COMM_TYPE ? await getPmsCommTypeName(locationId, num) : await getPmsUserName(locationId, num)
      return [key, name] as const
    })

    Promise.all(lookups).then((pairs) => {
      if (cancelled) return
      // One state write for the page rather than one per code, so a long feed
      // does not re-render once per lookup.
      const next: Record<string, string> = {}
      for (const [key, name] of pairs) if (name) next[key] = name
      setResolved(next)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey])

  return useMemo(
    () =>
      entries.map((entry) => {
        if (!entry.locationId) return entry
        const typeName = entry.commType ? resolved[keyFor(COMM_TYPE, entry.locationId, entry.commType)] : undefined
        const userName = entry.userNum ? resolved[keyFor(USER, entry.locationId, entry.userNum)] : undefined
        return { ...entry, commTypeName: typeName, userName }
      }),
    [entries, resolved],
  )
}
